/**
 * Résolution des slots « pièce d'identité » en mode HÉBERGEMENT pour la
 * PiecesGrid de la review.
 *
 * Contexte : quand l'acquéreur est hébergé chez un tiers, le dossier contient
 * DEUX pièces d'identité (acquéreur + hébergeur). Deux cas de dépôt :
 *   (a) fichier multi-doc (permis+CNI) → géré ailleurs via le warning OCR
 *       "contient_aussi_piece_identite" ;
 *   (b) fichiers DÉDIÉS — chacun dépose sa pièce séparément (cas HH-975-PP :
 *       CNI Esra + titre de séjour Murat). La table `cg_ocr_extractions` classe
 *       alors les DEUX en "piece_identite" sans distinguer les slots.
 *
 * Cette fonction couvre le cas (b) : elle identifie l'acquéreur (par le prénom
 * de la cession, comme `pickAcquereurPi` côté cross-checks) et renvoie la
 * pièce de l'HÉBERGEUR = une 2ᵉ pièce d'identité d'une AUTRE personne. Sans
 * ça, la grille marquait « Pièce d'identité de l'hébergeur MANQUANTE » alors
 * qu'elle est physiquement dans le dossier (le recap agence, lui, la route
 * correctement via le store). Pure — testable hors React.
 */

/**
 * Le routage (personne × type × face) s'est-il prononcé sur ce dossier ?
 *
 * Les deux recompositions d'hébergement de la PiecesGrid — le pattern
 * multi-doc et `resolveHebergementPiSlots` — sont des PRÉSOMPTIONS d'avant le
 * routage déterministe : elles devinent, par la présence d'une attestation ou
 * par le prénom de la cession, à qui appartient une pièce d'identité. Depuis
 * le routage, quelqu'un le SAIT, et son verdict arrive ici déjà substitué dans
 * `docTypeDetected` — un slot à rôle (hébergeur, co-titulaire), une face
 * explicite, ou le marqueur « rattachée à personne » ne peuvent venir que de
 * lui (ou d'une zone de dépôt dédiée, ce qui est un choix humain).
 *
 * Laisser les présomptions primer sur le routage le prend à contresens : sur
 * FK-371-EZ (18/09), la CNI de l'acquéreuse, correctement routée, était
 * déplacée d'office dans la vignette « hébergeur » — qui affichait donc le nom
 * de l'acquéreuse, la vignette du titulaire restant vide.
 *
 * Les dossiers antérieurs au routage ne portent aucun de ces marqueurs : ils
 * gardent les présomptions, qui restent leur seule information.
 */
const SLOT_A_ROLE = /^(piece_identite|permis_conduire)_(verso|hebergeur|cotitulaire)/;

export const routageIdentiteApplique = (
  extractions: ReadonlyArray<{ docTypeDetected: string; identityUnrouted?: boolean }>,
): boolean => extractions.some((e) => e.identityUnrouted === true || SLOT_A_ROLE.test(e.docTypeDetected));

export interface PiExtractionLite {
  docTypeDetected: string;
  docTypeMatch: boolean;
  confidence: number;
  fields: unknown;
}

/** Prénom normalisé (MAJUSCULES, trim) d'une extraction, ou '' si absent. */
const prenomOf = (e: PiExtractionLite): string => {
  const f = e.fields as Record<string, { value?: unknown } | undefined> | null | undefined;
  const v = f?.prenom?.value;
  return typeof v === 'string' ? v.trim().toUpperCase() : '';
};

export interface HebergementPiSlots<T extends PiExtractionLite> {
  /** Pièce d'identité de l'acquéreur (slot piece_identite). */
  acquereur: T | null;
  /** Pièce d'identité de l'hébergeur (slot piece_identite_hebergeur), ou null
   *  si une seule personne est présente (recto/verso) → pièce réellement absente. */
  hebergeur: T | null;
}

/**
 * Répartit les pièces d'identité déposées entre l'acquéreur et l'hébergeur.
 *
 * - L'acquéreur est la PI dont le prénom matche la cession (`acheteurPrenom`),
 *   à défaut la mieux notée.
 * - L'hébergeur est une AUTRE PI, d'une personne distincte (prénom non vide et
 *   différent de l'acquéreur). Un recto/verso de la même personne (verso sans
 *   prénom) n'est donc PAS pris pour un hébergeur.
 * - Renvoie `hebergeur: null` s'il n'y a qu'une seule personne → la grille
 *   conserve alors le statut « MANQUANTE » (pièce réellement absente).
 */
export const resolveHebergementPiSlots = <T extends PiExtractionLite>(
  extractions: T[],
  acheteurPrenom: string | null,
): HebergementPiSlots<T> => {
  const pis = extractions.filter((e) => e.docTypeDetected === 'piece_identite' && e.docTypeMatch);
  if (pis.length < 2) {
    return { acquereur: pis[0] ?? null, hebergeur: null };
  }

  const acheteur = (acheteurPrenom ?? '').trim().toUpperCase();
  const sorted = [...pis].sort((a, b) => b.confidence - a.confidence);
  const acquereur = (acheteur ? sorted.find((e) => prenomOf(e) === acheteur) : undefined) ?? sorted[0];
  const acquereurPrenom = prenomOf(acquereur);
  const hebergeur =
    sorted.find((e) => e !== acquereur && prenomOf(e) !== '' && prenomOf(e) !== acquereurPrenom) ?? null;

  return { acquereur, hebergeur };
};
